import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import currentTimeZone from '../../../../config/timeZone';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slices/ReportSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import NoDataAvailable from '../../../../shared-components/no-data-available/NoDataAvailable';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import report from '../../../../utils/api/reports';

interface IdleVsInuseSummary {
  totalUptimeSeconds: number;
  totalInuseSeconds: number;
  totalIdleSeconds: number;
}

interface PieDataItem {
  name: string;
  value: number;
  color: string;
  duration: string;
}

const IDLE_COLOR = '#4ABDFB';
const INUSE_COLOR = '#2AA700';

/**
 * ChargerReportRevenue component
 * Renders the account-wide idle vs in-use donut chart for the currently
 * selected account and date range, sourced from the charger-utilization
 * idle-vs-inuse endpoint (real device online time minus real charging
 * session time, consolidated across every charger on the account).
 */
function ChargerReportRevenue() {
  const { t } = useTranslation('report');
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const { accountId, reportFilter } = reportsData;
  const { startDate, endDate } = reportFilter;

  const [summary, setSummary] = useState<IdleVsInuseSummary>({
    totalUptimeSeconds: 0,
    totalInuseSeconds: 0,
    totalIdleSeconds: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * Fetches consolidated idle vs in-use seconds for the selected account and date range.
   */
  const fetchChargerUtilizationIdleVsInuseReport = async () => {
    if (!accountId || !startDate || !endDate) {
      return;
    }

    setLoading(true);
    try {
      const response = await report.fetchChargerUtilizationIdleVsInuseReport({
        accountCustomId: accountId,
        startDate,
        endDate,
        timeZone: currentTimeZone,
      });

      if (response.status === 200) {
        setSummary({
          totalUptimeSeconds: response.data?.totalUptimeSeconds || 0,
          totalInuseSeconds: response.data?.totalInuseSeconds || 0,
          totalIdleSeconds: response.data?.totalIdleSeconds || 0,
        });
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Custom tooltip component for chart hover.
   * @param {TooltipProps<number, string>} properties - The tooltip properties.
   * @returns {React.ReactElement | null} The rendered tooltip content, or null when inactive.
   */
  function CustomTooltip(properties: TooltipProps<number, string>) {
    const { active, payload } = properties;
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="connector-status-custom-tooltip">
          <p
            className="label"
            style={{
              color: data?.payload?.color,
            }}>{`${data.name} : ${data?.duration || FormatDuration(data?.value, { formatBy: 'sec', showDays: true, defaultValue: '00d 00h 00m 00s' })}`}</p>
        </div>
      );
    }
    return null;
  }

  /**
   * Handles pin click to toggle active state for a given report name.
   * @param {string} name - The name of the report pin to toggle.
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchChargerUtilizationIdleVsInuseReport();
  }, [accountId, startDate, endDate]);

  const totalSeconds = summary.totalIdleSeconds + summary.totalInuseSeconds;
  const isEmpty = totalSeconds <= 0;
  const totalHours = totalSeconds / 3600;

  const pieData: PieDataItem[] = [
    {
      name: t('idle'),
      value: summary.totalIdleSeconds,
      color: IDLE_COLOR,
      duration: FormatDuration(summary.totalIdleSeconds, {
        formatBy: 'sec',
        showDays: true,
        defaultValue: '00d 00h 00m 00s',
      }),
    },
    {
      name: t('inUse'),
      value: summary.totalInuseSeconds,
      color: INUSE_COLOR,
      duration: FormatDuration(summary.totalInuseSeconds, {
        formatBy: 'sec',
        showDays: true,
        defaultValue: '00d 00h 00m 00s',
      }),
    },
  ];

  /**
   * Renders the chart body based on loading/empty/loaded state.
   * @returns {React.ReactElement} The rendered chart content.
   */
  const renderChartContent = (): React.ReactElement => {
    if (loading) {
      return (
        <center className="loader-button">
          <BatteryLoader />
        </center>
      );
    }

    if (isEmpty) {
      return <NoDataAvailable />;
    }

    return (
      <>
        <div className="piechart-container">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                isAnimationActive={false} // Better for touch devices
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                wrapperStyle={{ zIndex: 1000 }} // Ensure tooltip appears above other elements
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="total-sessions-wrap">
            <p>{t('totalHours')}</p>
            <span>{totalHours.toFixed(1)}hrs</span>
          </div>
        </div>
        <div className="PieChart-heading">
          <div className="pie-chart-label vertical-legend">
            {pieData.map((eachData) => (
              <div key={eachData.name} className="each-label">
                <div
                  className="each-label-color"
                  style={{ '--label-color': eachData.color } as React.CSSProperties}
                />
                <p>{eachData.name}</p>
                <div className="each-label-value">
                  <span>{eachData.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="energy-report-chart-wrap charger-uptime-report">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin idle vs in-use report"
              className={`pin-button ${reportsData.activePinName.includes('uptimeVsInuse') ? 'active' : ''}`}
              onClick={() => handleClick('uptimeVsInuse')}>
              {reportsData.activePinName.includes('uptimeVsInuse') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('idle')} {t('vs')} {t('inUse')}
          </div>
        </div>

        <div className="energy-report-container chart-content-container">{renderChartContent()}</div>
      </div>
    </div>
  );
}

export default ChargerReportRevenue;
